import type {
    IBranchDeliveryZone,
    IBranchDeliveryZoneValue,
    IBranchMore,
    IBranchServiceMode,
    IBranchStatusValue,
    IBranchWeekday,
    ILocalizedBranchText,
    ITerminalGroup
} from "./BranchTypes"
import {translateUi} from "../../i18n/translateUi"

export const BRANCH_WEEKDAY_LABELS: Record<IBranchWeekday, string> = {
    get MONDAY() { return translateUi("Пн") },
    get TUESDAY() { return translateUi("Вт") },
    get WEDNESDAY() { return translateUi("Ср") },
    get THURSDAY() { return translateUi("Чт") },
    get FRIDAY() { return translateUi("Пт") },
    get SATURDAY() { return translateUi("Сб") },
    get SUNDAY() { return translateUi("Вс") }
}

const WEEKDAY_BY_DATE_INDEX: IBranchWeekday[] = [
    "SUNDAY",
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY"
]

const padTimeUnit = (value: number) => value.toString().padStart(2, "0")
type CoordinateRange = [number, number]

const UZBEKISTAN_LATITUDE_RANGE: CoordinateRange = [35, 47.5]
const UZBEKISTAN_LONGITUDE_RANGE: CoordinateRange = [55, 75]

const isInRange = (value: number, [min, max]: CoordinateRange) => value >= min && value <= max
const isLatitude = (value: number) => value >= -90 && value <= 90
const isLongitude = (value: number) => value >= -180 && value <= 180

export type NormalizedBranchZoneResult = {
    value: IBranchDeliveryZoneValue
    converted: boolean
    invalidIndex?: number
}

export const getBranchText = (
    value: ILocalizedBranchText,
    fallback = "—",
    language = "ru"
) => {
    if (typeof value === "string") return value.trim() || fallback
    if (!value || typeof value !== "object") return fallback

    return value[language]?.trim() ||
        value.ru?.trim() ||
        value.uz?.trim() ||
        value.en?.trim() ||
        fallback
}

export const getBranchLocalizedInitialValues = (value: ILocalizedBranchText) => {
    if (value && typeof value === "object") {
        return {
            ru: value.ru?.trim() || value.en?.trim() || "",
            uz: value.uz?.trim() || ""
        }
    }

    const text = getBranchText(value, "")
    return {
        ru: text,
        uz: text
    }
}

export const normalizeBranchDeliveryZoneValue = (zone?: unknown): NormalizedBranchZoneResult => {
    if (!Array.isArray(zone)) return {value: [], converted: false}

    let converted = false
    const value: IBranchDeliveryZoneValue = []

    for (let index = 0; index < zone.length; index += 1) {
        const point = zone[index]
        if (!Array.isArray(point) || point.length !== 2) {
            return {value: [], converted, invalidIndex: index}
        }

        const first = Number(point[0])
        const second = Number(point[1])
        if (!Number.isFinite(first) || !Number.isFinite(second)) {
            return {value: [], converted, invalidIndex: index}
        }

        const looksLikeUzbekistanLatLng = isInRange(first, UZBEKISTAN_LATITUDE_RANGE) &&
            isInRange(second, UZBEKISTAN_LONGITUDE_RANGE)
        const looksLikeUzbekistanLngLat = isInRange(first, UZBEKISTAN_LONGITUDE_RANGE) &&
            isInRange(second, UZBEKISTAN_LATITUDE_RANGE)

        if (looksLikeUzbekistanLngLat) {
            value.push([second, first])
            converted = true
            continue
        }

        if (looksLikeUzbekistanLatLng || (isLatitude(first) && isLongitude(second))) {
            value.push([first, second])
            continue
        }

        if (isLongitude(first) && isLatitude(second)) {
            value.push([second, first])
            converted = true
            continue
        }

        return {value: [], converted, invalidIndex: index}
    }

    return {value, converted}
}

export const toBranchDeliveryZoneApiValue = (zone?: IBranchDeliveryZoneValue | null): IBranchDeliveryZoneValue | null => {
    const normalizedZone = normalizeBranchDeliveryZoneValue(zone || [])

    if (!normalizedZone.value.length || normalizedZone.invalidIndex !== undefined) return null

    return normalizedZone.value.map(([latitude, longitude]) => [longitude, latitude])
}

export const getBranchDeliveryZoneValue = (deliveryZone?: IBranchDeliveryZone): IBranchDeliveryZoneValue => {
    if (!deliveryZone) return []
    if (Array.isArray(deliveryZone)) return normalizeBranchDeliveryZoneValue(deliveryZone).value
    return Array.isArray(deliveryZone.value) ? normalizeBranchDeliveryZoneValue(deliveryZone.value).value : []
}

export const getBranchEffectiveStatus = (branch: Pick<IBranchMore, "schedule">): IBranchStatusValue | null => (
    branch.schedule?.status || null
)

export const getBranchEffectiveServiceModes = (
    branch: Pick<IBranchMore, "schedule">
): IBranchServiceMode[] => {
    return branch.schedule?.service_mode || []
}

export const getBranchEffectiveOpenTime = (branch: Pick<IBranchMore, "schedule">) => (
    branch.schedule?.opening_time || null
)

export const getBranchEffectiveClosedTime = (branch: Pick<IBranchMore, "schedule">) => (
    branch.schedule?.closing_time || null
)

export const getBranchEffectiveWorkTime = (
    branch: Pick<IBranchMore, "schedule">
) => {
    const openTime = getBranchEffectiveOpenTime(branch)
    const closedTime = getBranchEffectiveClosedTime(branch)

    return openTime || closedTime ? `${openTime || "—"} - ${closedTime || "—"}` : ""
}

export const getBranchEffectiveDeliveryZoneValue = (
    branch: Pick<IBranchMore, "schedule">
): IBranchDeliveryZoneValue => {
    return normalizeBranchDeliveryZoneValue(branch.schedule?.delivery_zone || []).value
}

export const getCurrentBranchZoneFilter = (date = new Date()) => ({
    time: `${padTimeUnit(date.getHours())}:${padTimeUnit(date.getMinutes())}`,
    weekday: WEEKDAY_BY_DATE_INDEX[date.getDay()]
})

export const formatBranchWeekdays = (weekdayList: IBranchWeekday[]) => (
    weekdayList.length === 7
        ? translateUi("Пн-Вс")
        : weekdayList.map((weekday) => BRANCH_WEEKDAY_LABELS[weekday]).join(", ")
)

export type IBranchTerminalStatusResult = {
    groups: ITerminalGroup[]
    total: number
    aliveCount: number
    deadCount: number
    isFullyAlive: boolean
    hasDeadGroups: boolean
    deadGroups: ITerminalGroup[]
}

export const isTerminalGroupAlive = (
    group: ITerminalGroup,
    alivenessMap?: Record<string, boolean>
): boolean => {
    if (alivenessMap) {
        if (group.id && typeof alivenessMap[group.id] === "boolean") {
            return alivenessMap[group.id]
        }
        if (group.external_id && typeof alivenessMap[group.external_id] === "boolean") {
            return alivenessMap[group.external_id]
        }
        if (group.organization_id && typeof alivenessMap[group.organization_id] === "boolean") {
            return alivenessMap[group.organization_id]
        }
    }
    const directAlive = group.alive ?? group.is_alive ?? group.isAlive
    if (typeof directAlive === "boolean") {
        return directAlive
    }
    return true
}

export const getBranchTerminalGroups = (branch: Partial<IBranchMore>): ITerminalGroup[] => {
    if (Array.isArray(branch.terminal_groups)) return branch.terminal_groups
    if (Array.isArray(branch.terminal_group_list)) return branch.terminal_group_list
    return []
}

export const getBranchTerminalStatus = (
    branch: Partial<IBranchMore>,
    alivenessMap?: Record<string, boolean>
): IBranchTerminalStatusResult => {
    const rawGroups = getBranchTerminalGroups(branch)
    const groups = rawGroups.map((group) => ({
        ...group,
        alive: isTerminalGroupAlive(group, alivenessMap)
    }))
    const total = groups.length
    const deadGroups = groups.filter((g) => g.alive === false)
    const aliveGroups = groups.filter((g) => g.alive !== false)
    const deadCount = deadGroups.length
    const aliveCount = aliveGroups.length
    const hasDeadGroups = total > 0 && deadCount > 0
    const isFullyAlive = total > 0 && deadCount === 0

    return {
        groups,
        total,
        aliveCount,
        deadCount,
        isFullyAlive,
        hasDeadGroups,
        deadGroups
    }
}
